import React, { useState, useEffect } from 'react';
import {
  Table,
  Select,
  Button,
  Group,
  LoadingOverlay,
  Card,
  useMantineTheme,
  Text,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PROPOSALACTIONS } from '../../reducers/proposalReducer';
import { authWebSocket } from '../../../../auth/authFetches';
import { useAuth0 } from '@auth0/auth0-react';
import { Proposal, Lawyer, LawyerPosition } from '@/interfaces/types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconGripVertical } from '@tabler/icons-react';
import ExpandingTextArea from '../../../../components/ExpandingTextArea';
import { GenerationService } from '@/services/generationService';
import { useProposal, USERFEEDBACKACTIONS } from '../../context/ProposalContext';
import { getLawyersPositions } from '../../../../services/proposalsRoutes';
import { useGeneration } from '@/hooks/useGeneration';

type LawyerTableProps = {
  allLawyers: { LawyerName: string; Honorific: string }[];
  selectedLawyers: { name: string; bio: string }[];
};

const LawyerTable: React.FC<LawyerTableProps> = ({ allLawyers, selectedLawyers }) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const {
    proposal,
    dispatchProposal,
    userFeedback,
    dispatchUserFeedback,
    otherData,
    dispatchOtherData,
  } = useProposal();
  const { input, language, letterSenders } = proposal;
  const { loadingLawyerList, generating } = userFeedback;
  const { lawyerPositions } = otherData;
  const [additionalLawyers, setAdditionalLawyers] = useState<number>(0);

  // State for maxWidth of dropdown, active WebSocket and loading state
  const [maxWidth, setMaxWidth] = useState<string>('auto');

  const { withService } = useGeneration({
    dispatchProposal,
    dispatchUserFeedback,
    dispatchOtherData,
  });

  useEffect(() => {
    const longestName = allLawyers
      .map((lawyer) => lawyer.LawyerName)
      .reduce((longest, current) => (current.length > longest.length ? current : longest), '');
    setMaxWidth(`${longestName.length * 8}px`);
  }, [allLawyers]);

  // Add effect to fetch lawyer positions when lawyers change
  useEffect(() => {
    const fetchLawyerPositions = async () => {
      if (selectedLawyers.length === 0) return;

      try {
        const accessToken = await getAccessTokenSilently();
        const lawyerNames = selectedLawyers.map((lawyer) => lawyer.name);
        const positions = await getLawyersPositions(accessToken, lawyerNames);

        // Update lawyers with their positions
        const updatedLawyerPositions: Record<string, LawyerPosition> = {};
        positions.forEach((position: LawyerPosition & { LawyerName: string }) => {
          updatedLawyerPositions[position.LawyerName] = {
            ProfessionalTitlePl: position.ProfessionalTitlePl,
            ProfessionalTitleEn: position.ProfessionalTitleEn,
            PositionPl: position.PositionPl,
            PositionEn: position.PositionEn,
          };
        });
        dispatchOtherData({ field: 'lawyerPositions', value: updatedLawyerPositions });
        if (generating) {
          dispatchProposal({
            type: PROPOSALACTIONS.SORT_LAWYERS_BY_SENIORITY,
            payload: { lawyerPositions: updatedLawyerPositions },
          });
        }
      } catch (error) {
        console.error('Error fetching lawyer positions:', error);
      }
    };

    fetchLawyerPositions();
  }, [selectedLawyers.map((l) => l.name).join(',')]); // Only re-run when lawyer names change

  // useEffect to update the letter senders in case a lawyer gets removed
  useEffect(() => {
    const newLetterSenders = letterSenders.filter((lawyer) =>
      selectedLawyers.map((l) => l.name).includes(lawyer)
    );

    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'letterSenders',
      value: newLetterSenders,
    });
  }, [selectedLawyers]);

  const handleLawyerAddition = (lawyerName: string) => {
    if (lawyerName === '') {
      return;
    }
    dispatchProposal({
      type: PROPOSALACTIONS.ADD_LAWYER,
      payload: { name: lawyerName, bio: '' },
    });
    setAdditionalLawyers((prev) => prev - 1);
    handleGenerateBio(lawyerName);
  };

  const handleSelectChange = (lawyerName: string, rowIndex: number) => {
    dispatchProposal({
      type: PROPOSALACTIONS.EDIT_LAWYER,
      payload: { newName: lawyerName, oldName: selectedLawyers[rowIndex].name },
    });
    handleGenerateBio(lawyerName);
  };

  const handleBioChange = (lawyerName: string, bio: string) => {
    dispatchProposal({
      type: PROPOSALACTIONS.EDIT_LAWYER_BIO,
      payload: { name: lawyerName, bio },
    });
  };

  const removeRow = (lawyerName: string) => {
    dispatchProposal({ type: PROPOSALACTIONS.REMOVE_LAWYER, payload: lawyerName });
  };

  const getAvailableLawyers = (currentLawyerId: string) => {
    const selectedNames = selectedLawyers.map((lawyer) => lawyer.name);
    return allLawyers
      .map((lawyer) => lawyer.LawyerName)
      .filter((name) => name === currentLawyerId || !selectedNames.includes(name))
      .map((id) => ({ value: id, label: id }));
  };

  const handleGenerateBio = (lawyerName: string) => {
    withService(async (svc: GenerationService) => {
      await svc.generateBio(
        { input, documentLanguage: language, lawyerName },
        {
          dispatchProposal,
          dispatchUserFeedback,
          dispatchOtherData,
        }
      );
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = selectedLawyers.findIndex((lawyer) => lawyer.name === active.id);
    const newIndex = selectedLawyers.findIndex((lawyer) => lawyer.name === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reorderedLawyers = arrayMove(selectedLawyers, oldIndex, newIndex);
    dispatchProposal({
      type: PROPOSALACTIONS.REORDER_LAWYERS,
      payload: reorderedLawyers,
    });
  };

  const renderLawyerPosition = (lawyerName: string) => {
    const positions = lawyerPositions[lawyerName];
    if (positions) {
      const position = language == 'en' ? positions['PositionEn'] : positions['PositionPl'];
      const professionalTitle =
        language == 'en' ? positions['ProfessionalTitleEn'] : positions['ProfessionalTitlePl'];
      
      // Use position with fallback to professional title
      const displayTitle = position || professionalTitle;
      const isUsingFallback = !position && professionalTitle;
      
      return (
        <Text size="sm" c="dimmed" mb="xs">
          {displayTitle}
          {isUsingFallback && (
            <Text component="span" size="xs" c="orange" ml="xs">
              ({t('fallbackTitle')})
            </Text>
          )}
        </Text>
      );
    }
    return '';
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <SortableContext
        items={selectedLawyers.map((lawyer) => lawyer.name)}
        strategy={verticalListSortingStrategy}
      >
        <Table>
          <tbody>
            {selectedLawyers.map((lawyer, index) => (
              <SortableLawyerRow key={lawyer.name} id={lawyer.name}>
                {({ listeners, attributes }) => (
                  <>
                    <Group gap="xs" align="center">
                      <Select
                        placeholder={t('pickALawyer')}
                        data={getAvailableLawyers(lawyer.name)}
                        value={lawyer.name}
                        onChange={(value) => handleSelectChange(value || '', index)}
                        searchable
                        style={{ width: maxWidth, marginBottom: '0.5rem' }}
                      />
                      <div
                        style={{ cursor: 'grab', padding: '0.5rem' }}
                        {...listeners}
                        {...attributes}
                      >
                        <IconGripVertical size={20} />
                      </div>
                    </Group>

                    {lawyerPositions[lawyer.name] && renderLawyerPosition(lawyer.name)}

                    <div style={{ position: 'relative' }}>
                      <LoadingOverlay
                        visible={loadingLawyerList.includes(lawyer.name)}
                        zIndex={1}
                        overlayProps={{ radius: 'sm' }}
                      />
                      <ExpandingTextArea
                        value={selectedLawyers[index]?.bio || ''}
                        placeholder={
                          loadingLawyerList.includes(lawyer.name) ? t('loading') : t('addBio')
                        }
                        onChange={(event) => handleBioChange(lawyer.name, event.target.value || '')}
                        disabled={loadingLawyerList.includes(lawyer.name)}
                      />
                    </div>

                    <Button
                      variant="light"
                      color="red"
                      size="compact-xs"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => removeRow(lawyer.name)}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                      }}
                    >
                      <IconX size={16} />
                    </Button>
                  </>
                )}
              </SortableLawyerRow>
            ))}
            <AdditionalLawyers
              availableLawyers={getAvailableLawyers('')}
              handleLawyerAddition={handleLawyerAddition}
              additionalLawyers={additionalLawyers}
              setAdditionalLawyers={setAdditionalLawyers}
            />
          </tbody>
        </Table>
      </SortableContext>
    </DndContext>
  );
};

export default LawyerTable;
type SortableLawyerRowProps = {
  id: string;
  children: (dragHandleProps: { listeners: any; attributes: any }) => React.ReactNode;
};

export function SortableLawyerRow({ id, children }: SortableLawyerRowProps) {
  // Call useSortable only once per row
  const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({
    id,
  });
  const theme = useMantineTheme();

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <tr ref={setNodeRef} style={rowStyle}>
      <td>
        <Card
          withBorder
          my="xs"
          style={{
            position: 'relative',
            padding: '1rem',
          }}
        >
          {children({ listeners, attributes })}
        </Card>
      </td>
    </tr>
  );
}

export function AdditionalLawyers({
  availableLawyers,
  handleLawyerAddition,
  additionalLawyers,
  setAdditionalLawyers,
}: {
  availableLawyers: { value: string; label: string }[];
  handleLawyerAddition: (lawyerName: string) => void;
  additionalLawyers: number;
  setAdditionalLawyers: (value: number | ((prev: number) => number)) => void;
}) {
  const { t } = useTranslation();
  const add = () => setAdditionalLawyers((prev) => prev + 1);
  const remove = () => setAdditionalLawyers((prev) => Math.max(0, prev - 1));

  return (
    <>
      {Array.from({ length: additionalLawyers }).map((_, index) => (
        <tr key={index}>
          <td>
            <Card
              withBorder
              my="xs"
              style={{
                position: 'relative',
                padding: '1rem',
              }}
            >
              <Group gap="xs" align="center" pb="md">
                <Select
                  placeholder={t('pickALawyer')}
                  data={availableLawyers}
                  onChange={(value) => {
                    handleLawyerAddition(value || '');
                    remove();
                  }}
                  searchable
                />
              </Group>
              <ExpandingTextArea
                value=""
                placeholder={t('addBio')}
                disabled={true}
                onChange={() => {}}
              />
              <Button
                variant="light"
                color="red"
                size="compact-xs"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={remove}
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                }}
              >
                <IconX size={16} />
              </Button>
            </Card>
          </td>
        </tr>
      ))}
      <Button variant="light" onClick={add} mt="md">
        {t('addLawyer')}
      </Button>
    </>
  );
}
